/**
 * Printable money receipt (INCOME) / payment voucher (EXPENSE) – A5-ish, two copies optional.
 */
import React, { forwardRef } from "react";
import { TYPE_INFO, amountInWords, fmtDate, inr, partyName, userInfo } from "./ledgerUtils";

const css = `
.lr-wrap { font-family: Arial, Helvetica, sans-serif; color: #222; padding: 18px; }
.lr-copy { border: 1.5px solid #333; padding: 14px 18px; margin-bottom: 18px; page-break-inside: avoid; position: relative; }
.lr-head { display: flex; align-items: center; gap: 14px; border-bottom: 1.5px solid #333; padding-bottom: 8px; }
.lr-head img { width: 64px; height: 64px; object-fit: contain; }
.lr-school { flex: 1; text-align: center; }
.lr-school h2 { margin: 0; font-size: 20px; letter-spacing: .5px; }
.lr-school p { margin: 2px 0 0; font-size: 12px; }
.lr-title { text-align: center; margin: 8px 0; font-weight: bold; letter-spacing: 2px; font-size: 14px; }
.lr-title span { border: 1px solid #333; padding: 2px 12px; border-radius: 3px; }
.lr-row { display: flex; justify-content: space-between; font-size: 13px; margin: 4px 0; }
.lr-table { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 8px; }
.lr-table th, .lr-table td { border: 1px solid #555; padding: 6px 8px; }
.lr-table th { background: #f0f0f0; text-align: left; }
.lr-amt { text-align: right; white-space: nowrap; }
.lr-words { font-size: 12.5px; margin-top: 8px; }
.lr-sign { display: flex; justify-content: space-between; margin-top: 34px; font-size: 12.5px; }
.lr-sign div { border-top: 1px solid #333; padding-top: 3px; min-width: 150px; text-align: center; }
.lr-copytag { position: absolute; top: 6px; right: 10px; font-size: 10px; color: #666; }
.lr-cancel { position: absolute; top: 45%; left: 50%; transform: translate(-50%,-50%) rotate(-18deg);
  font-size: 54px; font-weight: bold; color: rgba(220,53,69,.28); border: 5px solid rgba(220,53,69,.28); padding: 0 18px; }
@media print { .lr-wrap { padding: 0; } }
`;

const Copy = ({ entry, label }) => {
  const u = userInfo();
  const info = TYPE_INFO[entry.entry_type] || TYPE_INFO.INCOME;
  const address = [u.companystreet, u.companycity, [u.companystate, u.companypincode].filter(Boolean).join("-")]
    .filter(Boolean)
    .join(", ");
  const isStudent = entry.party_type === "STUDENT";
  return (
    <div className="lr-copy">
      {label && <div className="lr-copytag">{label}</div>}
      {entry.status === "cancelled" && <div className="lr-cancel">CANCELLED</div>}
      <div className="lr-head">
        <img
          src={`/${u.tenantcode}-sidebar-logo.png`}
          alt=""
          onError={(e) => (e.target.style.visibility = "hidden")}
        />
        <div className="lr-school">
          <h2>{u.companyname}</h2>
          <p>{address}</p>
        </div>
        <div style={{ width: 64 }} />
      </div>
      <div className="lr-title"><span>{info.receiptTitle}</span></div>
      <div className="lr-row">
        <div><b>No.:</b> {entry.receipt_number}</div>
        <div><b>Date:</b> {fmtDate(entry.entry_date)}</div>
      </div>
      <div className="lr-row">
        <div>
          <b>{info.partyLabel}:</b> {partyName(entry)}
          {isStudent && entry.classname ? ` (Class ${entry.classname}${entry.sectionname ? " - " + entry.sectionname : ""}` +
            `${entry.admission_session ? ", " + entry.admission_session : ""})` : ""}
        </div>
        {entry.party_phone && <div><b>Phone:</b> {entry.party_phone}</div>}
      </div>
      <table className="lr-table">
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>Particulars</th>
            <th className="lr-amt" style={{ width: 140 }}>Amount (₹)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>1</td>
            <td>
              {entry.category_name}
              {entry.remark ? <div style={{ fontSize: 12, color: "#555" }}>{entry.remark}</div> : null}
            </td>
            <td className="lr-amt">{inr(entry.amount)}</td>
          </tr>
          <tr>
            <td colSpan={2} style={{ textAlign: "right" }}><b>Total</b></td>
            <td className="lr-amt"><b>{inr(entry.amount)}</b></td>
          </tr>
        </tbody>
      </table>
      <div className="lr-words"><b>In words:</b> {amountInWords(entry.amount)}</div>
      <div className="lr-row" style={{ marginTop: 6 }}>
        <div>
          <b>Mode:</b> {entry.payment_method || "Cash"}
          {entry.transaction_no ? ` (Ref: ${entry.transaction_no})` : ""}
        </div>
        {entry.session_year && <div><b>Session:</b> {entry.session_year}</div>}
      </div>
      {entry.status === "cancelled" && entry.cancel_reason && (
        <div className="lr-words" style={{ color: "#b02a37" }}><b>Cancelled:</b> {entry.cancel_reason}</div>
      )}
      <div className="lr-sign">
        <div>{entry.entry_type === "INCOME" ? "Received by" : "Receiver's Signature"}</div>
        <div>Authorised Signatory</div>
      </div>
    </div>
  );
};

const LedgerReceipt = forwardRef(({ entry, twoCopies = true }, ref) => {
  if (!entry) return <div ref={ref} />;
  const isIncome = entry.entry_type === "INCOME";
  return (
    <div ref={ref} className="lr-wrap">
      <style>{css}</style>
      <Copy entry={entry} label={twoCopies ? (isIncome ? "Payer's copy" : "Office copy") : ""} />
      {twoCopies && <Copy entry={entry} label={isIncome ? "Office copy" : "Receiver's copy"} />}
    </div>
  );
});

export default LedgerReceipt;
